'use client'

import Link from 'next/link'
import ButtonDefault from 'ui/src/buttons/ButtonDefault'
import ButtonIcon from 'ui/src/buttons/ButtonIcon'
import styles from '@/styles/components/partsGroups/ResearcherUserDetail.module.scss'
import profilestyles from '@/styles/components/partsGroups/ResearcherProfile.module.scss'
import inputstyles from 'ui/src/styles/components/parts/Input.module.scss'
import ModalPaperEdit from '@/components/partsGroups/modal/ModalPaperEdit'
import ModalAwardEdit from '@/components/partsGroups/modal/ModalAwardEdit'
import ModalConfirmDelete from '@/components/partsGroups/modal/ModalConfirmDelete'
import { useLocale } from '@/features/i18n/hooks/useLocale'
import { appUrls } from '@/libs/appUrls'
import useSWR from 'swr'
import { useAuth } from '@/components/providers/AuthProvider'
import { apiUrls } from '@/libs/apiUrls'
import { ResultMsg } from '@/libs/types/ResultMsg'
import { TResearchAchievement } from '@/libs/types/TResearchAchievement'
import { TPaper } from '@/libs/types/TPaper'
import { TPersonInfo } from '@/libs/types/TPersonInfo'
import { TJobInfo } from '@/libs/types/TJobInfo'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'

interface Inputs {
  researchAchievement: string
  orcIdUrl: string
  scienceDirectUrl: string
  googleScholarUrl: string
  researchGateUrl: string
}

export default function TableProfilePaperEdit({ className }: { className?: string }) {
  const { locale, t } = useLocale()
  const { token, tUser } = useAuth()
  const router = useRouter()

  const [isPaperEditModalOpen, setIsPaperEditModalOpen] = useState(false)
  const [isAchievementEditModalOpen, setIsAchievementEditModalOpen] = useState(false)
  const [isAchievementDeleteModalOpen, setIsAchievementDeleteModalOpen] = useState(false)
  const [isPaperDeleteModalOpen, setIsPaperDeleteModalOpen] = useState(false)
  const [paperData, setPaperData] = useState<(Paper & { id?: number })[]>([])
  const [editingPaperIndex, setEditingPaperIndex] = useState<number>()
  const [editingAchievementIndex, setEditingAchievementIndex] = useState<number>()
  const [deletePaperIndex, setDeletePaperIndex] = useState<number>()
  const [deleteAchievementIndex, setDeleteAchievementIndex] = useState<number>()
  const [editedPaperIds, setEditedPaperIds] = useState<number[]>([])
  const [editedAchievementIds, setEditedAchievementIds] = useState<number[]>([])
  const [deletedPaperIds, setDeletedPaperIds] = useState<number[]>([])
  const [deletedAchievementIds, setDeletedAchievementIds] = useState<number[]>([])
  const [inputs, setInputs] = useState<Inputs>({
    researchAchievement: '',
    orcIdUrl: '',
    scienceDirectUrl: '',
    googleScholarUrl: '',
    researchGateUrl: '',
  })
  const [achievementData, setAchievementData] = useState<TResearchAchievement[]>([]);

  const { data: papers, mutate: mutatePapers } = useSWR(
    token ? [apiUrls.personal.getPaperByPersonId(), token, tUser?.id] : null,
    async ([url, token, userId]) => {
      if (!token || !userId) {
        return
      }
      const data = {
        personId: userId,
        token,
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const result = (await response.json()) as ResultMsg
      return result.extend.paperList as TPaper[]
    },
  )

  const { data: achievements, mutate: mutateAchievements } = useSWR(
    token ? [apiUrls.personal.getAchievementByPersonId(), token, tUser?.id] : null,
    async ([url, token, userId]) => {
      if (!token || !userId) {
        return
      }
      const data = {
        personId: userId,
        token,
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const result = await response.json()
      return result.extend.achiList as TResearchAchievement[]
    },
  )

  const { data: userData, mutate: mutateUserData } = useSWR(
    token ? [apiUrls.personal.getPersonInfo(), token, tUser?.id] : null,
    async ([url, token, userId]) => {
      if (!token || !userId) {
        return
      }
      const urlParams = new URLSearchParams({ personId: userId, token })
      const response = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
      })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const result = (await response.json()) as ResultMsg<{
        personInfo: TPersonInfo
        jobInfoList: TJobInfo[]
        existingMail: string
      }>
      // console.log({ result })
      return result.extend
    },
  )


  const saveAction = async () => {
    if (!userData) {
      return
    }

    // 研究成果、研究者プラットフォーム
    const [surName, firstName] = userData.personInfo.name.split(' | ') // 取得したときにnullになっているのでフルネームから分割
    const personData: Partial<TPersonInfo> = {
      personId: tUser?.id,
      // 更新があるもののみ入れる
      researchSummary: inputs.researchAchievement,
      orcidUrl: inputs.orcIdUrl,
      scienceDirectUrl: inputs.scienceDirectUrl,
      gsUrl: inputs.googleScholarUrl,
      researchGateUrl: inputs.researchGateUrl,
    }
    const data = {
      token,
      id: tUser?.id,
      tPersonInfo: personData,
      surName,
      firstName,
      languageSkillArry: userData?.personInfo.language
        ? JSON.parse(userData?.personInfo.language)
        : [],
      professionalId: userData?.personInfo.professionalId
        ? JSON.parse(userData?.personInfo.professionalId)
        : [],
      organizationName: userData?.personInfo.organizationName,
      researchSkillArry: userData?.personInfo.researchSkill
        ? JSON.parse(userData?.personInfo.researchSkill)
        : [],
      department: userData?.personInfo.department,
      laboratory: userData?.personInfo.laboratory,
      position: userData?.personInfo.position,
      researchDirectState: 0, // (未使用) 0: 関連キーワード変更なし, 1: 変更あり
    }

    let newPersonInfo: TPersonInfo
    try {
      const response = await fetch(apiUrls.personal.savePersonInfo(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify(data),
      })
      const result = (await response.json()) as ResultMsg
      newPersonInfo = result.extend.tPersonInfo
    } catch (error) {
      // エラーメッセージを表示する場所がないので無視
      return
    }
    mutateUserData({ ...userData, personInfo: newPersonInfo })

    // 論文
    try {
      const paperList = paperData
        .filter((paper) => paper.id === undefined)
        .map((paper) => ({
          paperName: paper.paperTitle,
          paperUrl: paper.paperUrl,
          doi: paper.doi,
          paperStatus: 'Published', // 'Published' | 'PrePublic'
        }))
      const data = {
        token,
        personId: tUser?.id,
        paperList,
      }
      const response = await fetch(apiUrls.personal.uploadPaper(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify(data),
      })
      const result = (await response.json()) as ResultMsg
      if (result.status !== 1) {
        // 登録失敗
        // エラーメッセージを表示する場所がないので無視
        console.log(result.msg)
        return
      }
    } catch (error) {
      // エラーメッセージを表示する場所がないので無視
      return
    }

    // 受賞と資格
    try {
      const achievementList = achievementData
        ?.filter((achievement) => achievement.researchAchievementId === 0)
        .map((achievement) => ({
          researchAchievementName: achievement.researchAchievementName,
          researchAchievementPath: "",
          researchAchievementDate: achievement.researchAchievementDate,
        }))
      const data = {
        token,
        personId: tUser?.id,
        achievement: achievementList,
      }
      const response = await fetch(apiUrls.personal.upAchievement(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify(data),
      })
      const result = (await response.json()) as ResultMsg
      if (result.status !== 1) {
        // 登録失敗
        // エラーメッセージを表示する場所がないので無視
        console.log(result.msg)
        return
      }
    } catch (error) {
      // エラーメッセージを表示する場所がないので無視
      return
    }

    await Promise.all(
      [
        editedPaperIds.map(async (id) => {
          if (deletedPaperIds.includes(id)) {
            return
          }
    
          const paper = paperData.find((paper) => paper.id === id)!
          const data = {
            paperId: paper.id,
            doi: paper.doi,
            paperName: paper.paperTitle,
            paperUrl: paper.paperUrl,
            token,
          }
          try {
            const response = await fetch(apiUrls.personal.updatePaper(), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=utf-8',
              },
              body: JSON.stringify(data),
            })
            const result = (await response.json()) as ResultMsg
            if (result.status !== 1) {
              // 更新失敗
              // エラーメッセージを表示する場所がないので無視
              return
            }
          } catch (error) {
            // エラーメッセージを表示する場所がないので無視
            return
          }
        }),

        editedAchievementIds.map(async (id) => {
          // 更新
          if (deletedAchievementIds.includes(id)) {
            return
          }
    
          const achievement = achievementData.find((achievement) => achievement.researchAchievementId === id)!
          const data = {
            token,
            researchAchievementId: achievement.researchAchievementId,
            researchAchievementDate: achievement.researchAchievementDate,
            researchAchievementName: achievement.researchAchievementName,
          }
          try {
            const response = await fetch(apiUrls.personal.updateAchievement(), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=utf-8',
              },
              body: JSON.stringify(data),
            })
            const result = (await response.json()) as ResultMsg
            if (result.status !== 1) {
              // 更新失敗
              // エラーメッセージを表示する場所がないので無視
              return
            }
          } catch (error) {
            // エラーメッセージを表示する場所がないので無視
            return
          }
        }),

        deletedPaperIds.map(async (id) => {
          // 削除
          const data = {
            token,
            personId: tUser?.id,
            paperId: id,
          }
          try {
            const response = await fetch(apiUrls.personal.deletePaper(), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=utf-8',
              },
              body: JSON.stringify(data),
            })
            const result = (await response.json()) as ResultMsg
            if (result.status !== 1) {
              // 削除失敗
              // エラーメッセージを表示する場所がないので無視
              return
            }
          } catch (error) {
            // エラーメッセージを表示する場所がないので無視
            return
          }
        }),

        deletedAchievementIds.map(async (researchAchievementId) => {
          // 削除
          const data = {
            token,
            personId: tUser?.id,
            researchAchievementId,
          }
          try {
            const response = await fetch(apiUrls.personal.deleteAchievement(), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=utf-8',
              },
              body: JSON.stringify(data),
            })
            const result = (await response.json()) as ResultMsg
            if (result.status !== 1) {
              // 削除失敗
              // エラーメッセージを表示する場所がないので無視
              return
            }
          } catch (error) {
            // エラーメッセージを表示する場所がないので無視
            return
          }
        }),
      ].flat(),
    )
    mutatePapers()
    mutateAchievements()

    router.push(appUrls.paper(locale))
  }

  useEffect(() => {
    if (!papers) {
      return
    }
    setPaperData(
      papers.map((paper) => ({
        id: paper.paperId,
        paperUrl: paper.paperUrl,
        doi: paper.doi,
      })),
    )
  }, [papers])

  useEffect(() => {
    if (!userData) {
      return
    }
    setInputs({
      researchAchievement: userData.personInfo.researchSummary ?? '',
      orcIdUrl: userData.personInfo.orcidUrl ?? '',
      scienceDirectUrl: userData.personInfo.scienceDirectUrl ?? '',
      googleScholarUrl: userData.personInfo.gsUrl ?? '',
      researchGateUrl: userData.personInfo.researchGateUrl ?? '',
    })
  }, [userData])

  useEffect(() => {
    if (!achievements) {
      return
    }
    setAchievementData(
      achievements.map((achievement) => ({
        researchAchievementId: achievement.researchAchievementId,
        personId: achievement.personId,
        researchAchievementDate: achievement.researchAchievementDate,
        researchAchievementName: achievement.researchAchievementName,
        researchAchievementPath: achievement.researchAchievementPath || '',
      })),
    )
  }, [achievements])

  const updateAction = (newData: Paper) => {
    if (editingPaperIndex === undefined) {
      setPaperData([{ ...newData }, ...paperData])
    } else if (paperData[editingPaperIndex].id) {
      const id = paperData[editingPaperIndex].id!
      const newPaperData = [...paperData]
      newPaperData[editingPaperIndex] = { ...newData, id }

      setPaperData(newPaperData)
      setEditedPaperIds([...editedPaperIds, id])
    } else {
      const newPaperData = [...paperData]
      newPaperData[editingPaperIndex] = newData

      setPaperData(newPaperData)
    }
  }

  const updateAchievementAction = (newData: TResearchAchievement) => {
    if (editingAchievementIndex === undefined) {
      setAchievementData([{ ...newData, researchAchievementId: 0 }, ...achievementData])
    } else if (achievementData[editingAchievementIndex].researchAchievementId) {
      const id = achievementData[editingAchievementIndex].researchAchievementId!
      const newAchievementData = [...achievementData]
      newAchievementData[editingAchievementIndex] = { ...newData, researchAchievementId: id }

      setAchievementData(newAchievementData)
      setEditedAchievementIds([...editedAchievementIds, id])
    } else {
      const newAchievementData = [...achievementData]
      newAchievementData[editingAchievementIndex] = newData

      setAchievementData(newAchievementData)
    }
  }

  return (
    <>
      <div className={styles.userDetail__detail_box}>
        <h3 className={styles.userDetail__detail_box_title}>{t.profile.ResearchAchievement}</h3>
        <textarea
          name=""
          id=""
          className={`${inputstyles.inputTextArea} ${inputstyles.inputTextArea__l} ${inputstyles.gray} h-64`}
          value={inputs.researchAchievement}
          onChange={(e) => setInputs({ ...inputs, researchAchievement: e.target.value })}
        />
      </div>
      <div className={styles.userDetail__detail_box}>
        <h3 className={styles.userDetail__detail_box_title}>{t.profile.ResearcherPlatforms}</h3>
        <table className={`${inputstyles.form__regist_table} mb-5`}>
          <tbody>
            <tr>
              <th className="font-bold" style={{ width: '200px' }}>ORCID ID</th>
              <td>
                <input
                  type="text"
                  className={`${inputstyles.inputText} ${inputstyles.inputText__l} ${inputstyles.gray}`}
                  placeholder="https://orcid.org/0000-0001-2345-6789"
                  value={inputs.orcIdUrl}
                  onChange={(e) => setInputs({ ...inputs, orcIdUrl: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <th className="font-bold">ScienceDirect</th>
              <td>
                <input
                  type="text"
                  className={`${inputstyles.inputText} ${inputstyles.inputText__l} ${inputstyles.gray}`}
                  placeholder={t.profile.ScienceDirectPlaceholder}
                  value={inputs.scienceDirectUrl}
                  onChange={(e) => setInputs({ ...inputs, scienceDirectUrl: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <th className="font-bold">Google Scholar</th>
              <td>
                <input
                  type="text"
                  className={`${inputstyles.inputText} ${inputstyles.inputText__l} ${inputstyles.gray}`}
                  placeholder={t.profile.GoogleScholarPlaceholder}
                  value={inputs.googleScholarUrl}
                  onChange={(e) => setInputs({ ...inputs, googleScholarUrl: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <th className="font-bold">ResearchGate</th>
              <td>
                {<input
                  type="text"
                  className={`${inputstyles.inputText} ${inputstyles.inputText__l} ${inputstyles.gray}`}
                  placeholder={t.profile.ResearchGatePlaceholder}
                  value={inputs.researchGateUrl}
                  onChange={(e) => setInputs({ ...inputs, researchGateUrl: e.target.value })}
                />}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={styles.userDetail__detail_box}>
        <div className="flex justify-between align-center">
          <h3 className={styles.userDetail__detail_box_title}>{t.profile.UploadPapers}</h3>
          <ButtonDefault
            color="tertiary"
            className="font-bold"
            label={t.profile.AddPaper}
            action={() => {
              setEditingPaperIndex(undefined)
              setIsPaperEditModalOpen(true)
            }}
          />
        </div>
      </div>
      {paperData?.map((paper, index) => (
        <div
          key={paper.id ?? `new-${index}`}
          className={`${profilestyles.resercherProfile__paper} mb-4`}
        >
          <div className={profilestyles.resercherProfile__paper_body}>
            <p>{paper.paperUrl}</p>
            <dl>
              <dt>DOI</dt>
              <dd>{paper.doi}</dd>
            </dl>
          </div>
          <div className={profilestyles.resercherProfile__paper_btn}>
            <div className="flex gap-4">
              <ButtonDefault
                color="tertiary"
                className="font-bold"
                label={t.button.Edit}
                action={() => {
                  setEditingPaperIndex(index)
                  setIsPaperEditModalOpen(true)
                }}
              />
              <ButtonIcon
                variable="gabage"
                action={() => {
                  setDeletePaperIndex(index)
                  setIsPaperDeleteModalOpen(true)
                }}
              >
                <span className="icon icon-gabage"></span>
              </ButtonIcon>
            </div>
          </div>
        </div>
      ))}
      <div className={`${styles.userDetail__detail_box} mb-4`}>
        <div className="flex justify-between align-center">
          <h3 className={styles.userDetail__detail_box_title}>{t.profile.AwardQualification}</h3>
          <ButtonDefault
            color="tertiary"
            className="font-bold"
            label={t.profile.AddAwardQualification}
            action={() => {
              setEditingAchievementIndex(undefined)
              setIsAchievementEditModalOpen(true)
            }}
          />
        </div>
      </div>
      {achievementData?.map((achievement, index) => (
        <div
          key={achievement.researchAchievementId ?? `new-achievement-${index}`}
          className={`${profilestyles.resercherProfile__paper} mb-4`}
        >
          <div className={profilestyles.resercherProfile__paper_body}>
            <table>
              <tbody>
                <tr>
                  <th className="w-[100px]">{achievement.researchAchievementDate}</th>
                  <td>{achievement.researchAchievementName}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className={profilestyles.resercherProfile__paper_btn}>
            <div className="flex gap-4">
              <ButtonDefault
                color="tertiary"
                className="font-bold"
                label={t.button.Edit}
                action={() => {
                  setEditingAchievementIndex(index)
                  setIsAchievementEditModalOpen(true)
                }}
              />
              <ButtonIcon
                variable="gabage"
                action={() => {
                  setDeleteAchievementIndex(index)
                  setIsAchievementDeleteModalOpen(true)
                }}
              >
                <span className="icon icon-gabage"></span>
              </ButtonIcon>
            </div>
          </div>
        </div>
      ))}
      <hr className="hr mb-7 mt-10" />
      <div className="flex gap-5 justify-center">
        <ButtonDefault label={t.button.Cancel} color="secondary" href={appUrls.paper(locale)} />
        <ButtonDefault label={t.button.Save} color="primary" action={saveAction} />
      </div>
      {/* 研究成果編集モーダル */}
      <ModalPaperEdit
        isOpen={isPaperEditModalOpen}
        data={editingPaperIndex !== undefined ? paperData[editingPaperIndex] : undefined}
        onClose={() => setIsPaperEditModalOpen(false)}
        saveAction={updateAction}
      />
      {/* 受賞と資格の編集モーダル */}
      <ModalAwardEdit
        isOpen={isAchievementEditModalOpen}
        data={editingAchievementIndex !== undefined ? achievementData[editingAchievementIndex] : undefined}
        onClose={() => setIsAchievementEditModalOpen(false)}
        saveAction={updateAchievementAction}
      />
      {/* 削除確認モーダル(論文（アップロード）) */}
      <ModalConfirmDelete
        isOpen={isPaperDeleteModalOpen}
        onClose={() => {
          setDeletePaperIndex(undefined)
          setIsPaperDeleteModalOpen(false)
        }}
        deleteAction={() => {
          const index = deletePaperIndex!
          const paper = paperData[index]
          setPaperData(paperData.toSpliced(index, 1))
          if (paper.id !== undefined) {
            setDeletedPaperIds([...deletedPaperIds, paper.id])
          }
          setDeletePaperIndex(undefined)
        }}
      />
      {/* 削除確認モーダル(受賞資格) */}
      <ModalConfirmDelete
        isOpen={isAchievementDeleteModalOpen}
        onClose={() => {
          setDeleteAchievementIndex(undefined)
          setIsAchievementDeleteModalOpen(false)
        }}
        deleteAction={() => {
          const index = deleteAchievementIndex!
          const achievement = achievementData[index]
          setAchievementData(achievementData.toSpliced(index, 1))
          if (achievement.researchAchievementId !== undefined) {
            setDeletedAchievementIds([...deletedAchievementIds, achievement.researchAchievementId])
          }
          setDeleteAchievementIndex(undefined)
        }}
      />
    </>
  )
}
